import { Injectable } from '@angular/core';
import { DigitalMemoryLog, TableColumn } from '../models/digital-memory-log.interface';
import { CopyLinkColComponent } from '../components/copy-link-col/copy-link-col.component';
import { DigitalLogActionColComponent } from '../components/digital-log-action-col/digital-log-action-col.component';
import { ExpiryDateColComponent } from '../components/expiry-date-col/expiry-date-col.component';
import { DIGITAL_MEMORY_LOG_CONSTANTS } from '../constants/digital-memory-log.constants';
import { CTableConfig } from 'src/app/core/ui/c-table/c-table-config';
import { enrichDigitalMemoryLogExpiry, isDigitalMemoryLogExpired } from '../utils/digital-memory-log.utils';

@Injectable({
  providedIn: 'root'
})
export class DigitalMemoryLogTableService {

  /**
   * Get table column definitions
   */
  getColumnDefs(): TableColumn[] {
    return [
      {
        displayName: 'Digital Memory Log Name',
        field: 'name',
        cellClass: DIGITAL_MEMORY_LOG_CONSTANTS.CSS_CLASSES.COLUMN_NAME,
        cellHeaderClass: 'text-inline column-name',
      },
      {
        displayName: 'Creation Date',
        field: 'createdAt',
        cellPipe: 'cDate',
        cellClass: DIGITAL_MEMORY_LOG_CONSTANTS.CSS_CLASSES.TEXT_INLINE,
        cellHeaderClass: 'text-inline',
      },
      {
        displayName: 'Last Updated',
        field: 'updatedAt',
        cellPipe: 'cDate',
        cellClass: DIGITAL_MEMORY_LOG_CONSTANTS.CSS_CLASSES.TEXT_INLINE,
        cellHeaderClass: 'text-inline',
      },
      {
        displayName: 'Expiry Date',
        field: 'expiryDate',
        cellComponent: ExpiryDateColComponent,
        cellClass: DIGITAL_MEMORY_LOG_CONSTANTS.CSS_CLASSES.TEXT_INLINE,
        cellHeaderClass: 'text-inline',
      },
      {
        displayName: 'Copy Link',
        cellComponent: CopyLinkColComponent,
        cellHeaderClass: 'text-inline column-name',
        cellClass: 'text-inline column-name',
      },
      {
        displayName: 'Total Sent',
        field: 'totalEmailCount',
        cellClass: DIGITAL_MEMORY_LOG_CONSTANTS.CSS_CLASSES.TEXT_INLINE,
        cellHeaderClass: 'text-inline',
      },
      {
        cellClass: DIGITAL_MEMORY_LOG_CONSTANTS.CSS_CLASSES.ACTION_STICKY,
        cellHeaderClass: 'action-sticky text-inline',
        displayName: 'Action',
        cellComponent: DigitalLogActionColComponent,
      },
    ];
  }

  /**
   * Get default table options
   */
  getDefaultTableOptions(): Partial<CTableConfig> {
    return {
      useLocalTable: false,
      usePaging: true,
      pagingOptions: {
        showTotalRecords: true,
        pageSizes: DIGITAL_MEMORY_LOG_CONSTANTS.TABLE.PAGE_SIZES,
        pageSize: DIGITAL_MEMORY_LOG_CONSTANTS.TABLE.DEFAULT_PAGE_SIZE,
        currentPage: 1,
      },
      isAutoload: true,
      columnDefs: this.getColumnDefs(),
      rowClass: (row: any) => {
        const classes = ['c-row'];
        if (row && row.entity && isDigitalMemoryLogExpired(row.entity)) {
          classes.push('dml-row-expired');
        }
        return classes.join(' ');
      },
    };
  }

  /**
   * Process digital memory log data to add calculated fields
   */
  processDigitalMemoryLogData(data: DigitalMemoryLog[]): DigitalMemoryLog[] {
    return data.map((item: DigitalMemoryLog) => {
      enrichDigitalMemoryLogExpiry(item);
      return item;
    });
  }

  /**
   * Create table extra data object with action handlers
   */
  createExtraData(
    actionsService: any,
    shipId: string,
    shipName: string,
    route: any,
    onRefresh?: () => void
  ): any {
    return {
      sendMailbyId: (entity: DigitalMemoryLog) => {
        actionsService.sendMailById(entity, shipName, onRefresh);
      },
      multiSendMailbyId: (entity: DigitalMemoryLog) => {
        actionsService.multiSendMailById(entity, shipName, onRefresh);
      },
      copyLink: (entity: DigitalMemoryLog) => {
        actionsService.copyLink(entity);
      },
      renameDigitalLog: (entity: DigitalMemoryLog) => {
        actionsService.renameDigitalLog(entity, onRefresh);
      },
      onDuplicateSuccess: (entity: DigitalMemoryLog) => {
        if (onRefresh) {
          onRefresh();
        }
      },
      editDigitalLog: (entity: DigitalMemoryLog) => {
        actionsService.editDigitalLog(entity, shipId, shipName);
      },
      previewDigitalLog: (entity: DigitalMemoryLog) => {
        actionsService.previewDigitalLog(entity, route);
      },
    };
  }
}
